<template>
  <BuscaServidorForm
    v-bind:readonly="readonly || loading"
    v-bind:value="value"
    v-bind:regimesJuridicos="regimesJuridicos"
    v-bind:situacoesFuncionais="situacoesFuncionais"
    v-on:submit="onSubmit"
  />
</template>

<script>
/**
 * @module components/BuscaServidor/BuscaServidor
 * @category Componentes
 * @summary _Single File Component_ (SFC) de busca de servidores.
 *
 * @description
 * Este componente disponibiliza buscas de resultado único por Siape e CPF,
 * além de busca com múltiplos resultados por nome e cargo. Todas as formas de
 * busca já vêm paginadas.
 *
 * Este componente não possui _slot_ _default_.
 *
 * @requires module:components/common/BuscaServidorForm/BuscaServidorForm
 * @requires module:constants.API_URL
 * @requires module:utils/lang.isEmpty
 * @requires module:utils/lang.isFunction
 * @requires module:utils/lang.isNullOrUndefined
 *
 * @vue-prop {string} [id] - Atributo `id` para identificação do componente.
 * Utilizado também para identificar unicamente cada campo do componente.
 * @vue-prop {boolean} [readonly] - Indica se o componente estará em modo
 * somente-leitura.
 * @vue-prop {number} [size=20] - Tamanho da página para as buscas.
 * @vue-prop {function} [onSearchStart] - Função que é chamada quando a busca
 * pelo servidor é iniciada.
 * @vue-prop {function} [onSearchEnd] - Função que é chamada quando a busca
 * pelo servidor é finalizada.
 * @vue-prop {object} [value] - Valor inicial do componente.
 * Vide {@link module:components/common/BuscaServidorForm/BuscaServidorForm BuscaServidorForm}.
 * @vue-event {object} onLoad - Emite o resultado da busca por servidores, na
 * estrutura de páginas de busca do SisPessoal.
 * @vue-event {external:Error} onError - Emite o erro ocorrido na busca por
 * servidores.
 *
 * @example
 * <BuscaServidor
 *  v-bind="{ size: 10, value: { tipo: 'siape', values: [{ name: 'siape', value: '1234567' }] } }"
 *  v-on:load="exibeServidores"
 * />
 */
import BuscaServidorForm from "../BuscaServidorForm/BuscaServidorForm.vue";
import { API_URL } from "../../../constants";
import { isEmpty, isFunction, isNullOrUndefined } from "../../../utils";

const PAGINATION_PAGE_START = 0;
const PAGINATION_PAGE_SIZE = 10;

const SEARCH_BY_SIAPE = "siape";
const SEARCH_BY_CPF = "cpf";
const SEARCH_BY_FILTROS = "filtros";

export default {
  name: "BuscaServidor",
  components: {
    BuscaServidorForm
  },
  inheritAttrs: false,
  props: {
    id: String,

    readonly: Boolean,

    size: {
      type: Number,
      default: PAGINATION_PAGE_SIZE
    },

    onSearchStart: Function,
    onSearchEnd: Function,

    value: Object
  },
  data() {
    return {
      loading: false,
      regimesJuridicos: [],
      situacoesFuncionais: [],
      searchParams: null
    };
  },
  watch: {
    value: {
      immediate: true,

      handler(newValue) {
        this.searchParams = newValue;
      }
    }
  },
  mounted() {
    this.loading = true;

    const regime = this.$http
      .get(`${API_URL}/core/regimeJuridico`)
      .then(response => {
        this.regimesJuridicos = response.data;
      })
      .catch(error => this.$emit("error", error));

    const situacao = this.$http
      .get(`${API_URL}/core/situacaoFuncional`)
      .then(response => {
        this.situacoesFuncionais = response.data;
      })
      .catch(error => this.$emit("error", error));

    Promise.all([regime, situacao]).finally(() => {
      this.loading = false;
    });
  },
  methods: {
    search(page = PAGINATION_PAGE_START, size = this.size) {
      if (this.loading) {
        return this.$notification.pushInfo(
          "Por favor, aguarde a busca anterior ser finalizada"
        );
      }

      this.loading = true;

      if (isFunction(this.onSearchStart)) {
        this.onSearchStart();
      }

      const querystring = [];

      if (!isEmpty(this.searchParams)) {
        Object.entries(this.searchParams).forEach(item => {
          if (!isNullOrUndefined(item[1])) {
            querystring.push(`${item[0]}=${item[1]}`);
          }
        });
      }

      if (!isNullOrUndefined(page)) {
        querystring.push(`pagina=${page}`);
      }

      if (!isNullOrUndefined(size)) {
        querystring.push(`tamanhoPagina=${size}`);
      }

      const url = `${API_URL}/core/servidor/v2?${querystring.join("&")}`;

      this.$http
        .get(url)
        .then(response => this.$emit("load", response.data))
        .catch(error => this.$emit("error", error))
        .finally(() => {
          this.loading = false;

          if (isFunction(this.onSearchEnd)) {
            this.onSearchEnd();
          }
        });
    },
    onSubmit(event) {
      const {
        tipo,
        siape,
        cpf,
        nome,
        incluirInativos,
        cargo,
        localizacao,
        lotacao,
        hierarquiaCompleta,
        regimeJuridico,
        situacaoFuncional
      } = event;

      const params = {};

      switch (tipo) {
        case SEARCH_BY_SIAPE:
          if (!siape || window.isNaN(window.parseInt(siape))) {
            return this.$notification.pushError("Matrícula Siape inválida");
          }

          params.siape = siape;
          break;
        case SEARCH_BY_CPF:
          if (!cpf || window.isNaN(window.parseInt(cpf))) {
            return this.$notification.pushError("CPF inválido");
          }

          params.cpf = cpf;
          break;
        case SEARCH_BY_FILTROS:
          params.nome = nome;
          params.cargo = cargo;
          params.localizacaoCodigo = localizacao;
          params.lotacaoCodigo = lotacao;
          params.incluirInativos = incluirInativos;
          params.hierarquiaCompleta = hierarquiaCompleta;
          params.regime = regimeJuridico;
          params.situacaoFuncional = situacaoFuncional;
          break;
        default:
          return this.$notification.pushError(
            "Por favor, escolha um tipo de busca válido"
          );
      }

      this.searchParams = params;

      return this.search(PAGINATION_PAGE_START, this.size);
    }
  }
};
</script>
